import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, InputNumber, Modal, Row, Space, message } from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import AiCreditPurchaseService, { AiCreditPurchaseOptions } from '../../services/AiCreditPurchaseService';
import BillingDetailsFields, { InvoiceType } from '../Payments/BillingDetailsFields';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);

const AiCreditPurchaseModal: React.FC = () => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [options, setOptions] = useState<AiCreditPurchaseOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('personal');
  const creditAmount = Form.useWatch('creditAmount', form) || 1;
  const totalAmount = useMemo(
    () => Number(options?.unitPrice ?? 0) * Number(creditAmount || 0),
    [options?.unitPrice, creditAmount],
  );

  const loadOptions = () => {
    setLoadingOptions(true);
    AiCreditPurchaseService.getOptions()
      .then(({ data }) => setOptions(data))
      .catch(() => {
        setOptions({ available: false, unitPrice: null });
        message.error('Kredi satış bilgisi yüklenemedi.');
      })
      .finally(() => setLoadingOptions(false));
  };

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    if (visible) {
      loadOptions();
    }
  }, [visible]);

  const startPurchase = (values: any) => {
    const request = {
      creditAmount: Number(values.creditAmount),
      name: values.firstName,
      surname: values.lastName,
      identityNumber: invoiceType === 'personal' ? values.identityNumber : undefined,
      companyName: invoiceType === 'corporate' ? values.companyName : undefined,
      taxNumber: invoiceType === 'corporate' ? values.taxNumber : undefined,
      taxOffice: invoiceType === 'corporate' ? values.taxOffice : undefined,
      phone: values.phone,
      email: values.email,
      city: values.city,
      billingAddress: values.billingAddress,
    };
    setSubmitting(true);
    AiCreditPurchaseService.initializePurchase(request)
      .then(({ data }) => {
        localStorage.setItem('sahaPaymentDestination', 'ai-credit');
        localStorage.setItem('sahaAiCreditPurchaseId', String(data.purchaseId));
        setVisible(false);
        const wrapper = document.createElement('div');
        wrapper.innerHTML = data.htmlContent;
        Array.from(wrapper.getElementsByTagName('script')).forEach((sourceScript) => {
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.innerHTML = sourceScript.innerHTML;
          document.body.appendChild(script);
        });
      })
      .catch((error) => {
        const detail = error?.response?.data?.message || error?.response?.data?.detail;
        message.error(detail || 'Kredi satın alma işlemi başlatılamadı.');
      })
      .finally(() => setSubmitting(false));
  };

  const isAvailable = options?.available === true && Number(options.unitPrice) > 0;

  return (
    <>
      <Button
        icon={<CreditCardOutlined />}
        onClick={() => setVisible(true)}
        disabled={loadingOptions || !options?.available}
        title={!options?.available ? 'Yapay zekâ kredisi satışı şu anda etkin değil.' : undefined}
      >
        Kredi Satın Al
      </Button>
      <Modal
        visible={visible}
        title="Yapay Zekâ Sohbet Kredisi Satın Al"
        onCancel={() => setVisible(false)}
        footer={null}
        width={760}
        destroyOnClose
      >
        {loadingOptions && <Alert type="info" showIcon message="Güncel kredi fiyatı yükleniyor." />}
        {!loadingOptions && !isAvailable && (
          <Alert type="warning" showIcon message="Yapay zekâ kredisi satışı şu anda etkin değil." />
        )}
        {isAvailable && (
          <Form form={form} layout="vertical" onFinish={startPurchase} initialValues={{ creditAmount: 1 }}>
            <Row gutter={16} align="bottom">
              <Col xs={24} md={8}>
                <Form.Item name="creditAmount" label="Kredi adedi" rules={[{ required: true, message: 'Kredi adedini girin.' }, { validator: (_, value) => Number.isInteger(value) && value >= 1 ? Promise.resolve() : Promise.reject(new Error('Pozitif tam sayı girin.')) }]}>
                  <InputNumber min={1} precision={0} className="w-full" />
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <div style={{ paddingBottom: 24 }}>
                  <strong>1 kredi: {formatCurrency(Number(options.unitPrice))}</strong><br />
                  <span>Toplam (KDV dâhil): <strong>{formatCurrency(totalAmount)}</strong></span>
                </div>
              </Col>
            </Row>
            <BillingDetailsFields invoiceType={invoiceType} onInvoiceTypeChange={setInvoiceType} />
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setVisible(false)}>Vazgeç</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>Ödeme Yap</Button>
            </Space>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default AiCreditPurchaseModal;
